import { apiFetch } from "./client";

export interface ChatSession {
  id: string;
  companyId: string;
  agentId: string;
  channelId: string | null;
  userId: string | null;
  title: string;
  createdAt: string;
  updatedAt: string;
  // Joined from agent_channels
  channelName: string | null;
  channelColor: string | null;
  channelEmoji: string | null;
  channelSlug: string | null;
  /** True while an assistant response is still being generated for this session. */
  isGenerating: boolean;
  /** True when a completed assistant response has not been opened by this user. */
  hasUnread: boolean;
}

export interface ChatFileAttachment {
  id: string;
  name: string;
  mimeType: string;
  size: number;
  url: string;
}

/**
 * One tool invocation in an assistant turn, persisted on the message so the
 * "N steps" trace survives completion and reload. `args`/`result`/`error` are
 * capped strings (full-ish input JSON, tool output, failure reason).
 */
export interface ToolStepMeta {
  toolUseId: string;
  name: string;
  status: "started" | "completed" | "failed";
  args?: string;
  result?: string;
  error?: string;
  durationMs?: number;
  timestamp?: number;
}

export interface ChatMessage {
  id: string;
  sessionId: string;
  role: "user" | "assistant";
  content: string;
  respondingAgentId: string | null;
  /** better-auth user id of whoever posted this message. Populated for
   *  role='user' rows; null for system/assistant. Drives per-message
   *  profile photo lookup in shared channels. */
  senderUserId?: string | null;
  /** Display name of the sender (joined from auth_users.name). Used for
   *  initials fallback when the photo can't be loaded. */
  senderName?: string | null;
  metadata: {
    attachments?: ChatFileAttachment[];
    respondingAgentName?: string;
    respondingAgentAvatarUrl?: string | null;
    /**
     * Token usage from the agent's runtime. Populated on assistant rows
     * after the turn completes (the platform forwards a {type:"usage"}
     * SSE event from the runtime and persists the payload here).
     * Used by the chat header's ContextDonut to show how full the
     * model's context window is.
     */
    usage?: {
      input_tokens?: number;
      output_tokens?: number;
      cache_creation_input_tokens?: number;
      cache_read_input_tokens?: number;
    };
    /** Model id the runtime reported the usage against, e.g. "claude-sonnet-4-6". */
    usageModel?: string;
    /** True while the assistant turn is still streaming server-side. The
     *  row is pre-inserted empty so polling clients can see the in-flight
     *  turn — render it as a working indicator, never as a blank bubble. */
    streaming?: boolean;
    /** Liveness signal (epoch ms) written every ~30s while streaming. */
    streamingHeartbeatAt?: number;
    /** Set by the server when the turn died before any content arrived. */
    interrupted?: boolean;
    /** Epoch milliseconds when a pre-inserted streaming row actually finished. */
    completedAt?: number;
    /**
     * Tool activity for this assistant turn — persisted so the completed
     * message keeps a collapsible "N steps" section with each step's args,
     * result and error. Empty/absent for turns that used no tools.
     */
    toolSteps?: ToolStepMeta[];
  } | null;
  createdAt: string;
}

/** Response shape of the windowed messages fetch (`?limit=`). `hasMore` is
 *  true when rows older than the returned page exist. */
export interface ChatMessagesWindow {
  messages: ChatMessage[];
  hasMore: boolean;
}

export interface DebugSession {
  id: string;
  title: string;
  channelId: string | null;
  userId: string | null;
  createdAt: string;
  updatedAt: string;
  channelName: string | null;
  channelShared: boolean | null;
  userEmail: string | null;
  messageCount: number;
  lastMessageAt: string | null;
}

export const chatApi = {
  listSessions: (agentId: string) =>
    apiFetch<ChatSession[]>(`/chat/sessions?agentId=${agentId}`),
  /** Admin-only diagnostic — lists EVERY session for an agent. */
  debugSessions: (agentId: string) =>
    apiFetch<DebugSession[]>(`/chat/debug-sessions?agentId=${agentId}`),
  /** Admin-only — deletes every session for the agent that has zero messages. */
  debugDeleteEmptySessions: (agentId: string) =>
    apiFetch<{ deleted: number }>(`/chat/debug-delete-empty-sessions?agentId=${agentId}`, {
      method: "POST",
    }),
  createSession: (companyId: string, agentId: string, channelId?: string) =>
    apiFetch<ChatSession>("/chat/sessions", {
      method: "POST",
      body: JSON.stringify({ companyId, agentId, channelId }),
    }),
  deleteSession: (sessionId: string) =>
    apiFetch<void>(`/chat/sessions/${sessionId}`, { method: "DELETE" }),
  renameSession: (sessionId: string, title: string) =>
    apiFetch<ChatSession>(`/chat/sessions/${sessionId}`, {
      method: "PATCH",
      body: JSON.stringify({ title }),
    }),
  getMessages: (sessionId: string) =>
    apiFetch<ChatMessage[]>(`/chat/sessions/${sessionId}/messages`),
  /** Windowed transcript fetch — the newest `limit` messages (ascending),
   *  optionally strictly older than `beforeId`. The server returns the
   *  paged { messages, hasMore } shape only for this parameterised form;
   *  plain getMessages keeps the legacy full-array response. */
  getMessagesWindow: (sessionId: string, opts: { limit: number; beforeId?: string }) =>
    apiFetch<ChatMessagesWindow>(
      `/chat/sessions/${sessionId}/messages?limit=${opts.limit}${
        opts.beforeId ? `&beforeId=${encodeURIComponent(opts.beforeId)}` : ""
      }`,
    ),
  sendMessage: (sessionId: string, content: string) =>
    apiFetch<{ userMessage: ChatMessage; assistantMessage: ChatMessage | null }>(
      `/chat/sessions/${sessionId}/messages`,
      { method: "POST", body: JSON.stringify({ content }) },
    ),
};
